import {
  Flex,
  FlexColumn,
  FormField,
  Switch,
  TextField,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { VersionControl } from 'shared/types';
import { fontFamily } from '@elementary/common-ui/style/utils';

const ELEMENTARY_PREFIX = 'elementary';
const BRANCH_BASE_PLACEHOLDER = 'fix-model';
const TICKET_PLACEHOLDER = 'TICKET-123';
const COMMIT_MSG_PLACEHOLDER = 'fix: updated dbt model';

function buildBranchPreview(
  branchPrefix: string,
  requireTicketId: boolean,
): string {
  let prefix = branchPrefix.trim();
  if (prefix && !prefix.endsWith('/')) {
    prefix += '/';
  }
  let branch = `${prefix}${ELEMENTARY_PREFIX}-${BRANCH_BASE_PLACEHOLDER}`;
  if (requireTicketId) {
    branch = `${TICKET_PLACEHOLDER}/${branch}`;
  }
  return branch;
}

function buildTitlePreview(requireTicketId: boolean): string {
  const base = `Elementary: fix null values in orders`;
  return requireTicketId ? `[${TICKET_PLACEHOLDER}] ${base}` : base;
}

function buildCommitPreview(commitMessagePrefix: string): string {
  const trimmed = commitMessagePrefix.trim();
  return trimmed
    ? `${trimmed} ${COMMIT_MSG_PLACEHOLDER}`
    : COMMIT_MSG_PLACEHOLDER;
}

type Props = {
  selectedVersionControl: VersionControl | undefined;
  addLabels: boolean;
  onAddLabelsChange: (value: boolean) => void;
  branchPrefix: string;
  onBranchPrefixChange: (value: string) => void;
  commitMessagePrefix: string;
  onCommitMessagePrefixChange: (value: string) => void;
  requireTicketId: boolean;
  onRequireTicketIdChange: (value: boolean) => void;
  isLoading?: boolean;
};

const PreviewLine = ({ title, value }: { title: string; value: string }) => (
  <Flex sx={{ alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
    <Typography
      variant="caption"
      color="content.translucent.secondary"
      sx={{ flexShrink: 0 }}
    >
      {title}:
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontFamily: fontFamily('monoSystem'),
        color: 'content.translucent.primary',
        wordBreak: 'break-all',
      }}
    >
      {value}
    </Typography>
  </Flex>
);

const SettingsSection = ({
  selectedVersionControl,
  addLabels,
  onAddLabelsChange,
  branchPrefix,
  onBranchPrefixChange,
  commitMessagePrefix,
  onCommitMessagePrefixChange,
  requireTicketId,
  onRequireTicketIdChange,
  isLoading = false,
}: Props) => (
  <FlexColumn sx={{ gap: 3 }}>
    <Typography variant="subtitle1" fontWeight="bold">
      Settings
    </Typography>

    {selectedVersionControl === 'GitHub' && (
      <FormField
        title="Add labels"
        description="Automatically add labels to pull requests opened by Elementary"
        optional
      >
        <Flex sx={{ alignItems: 'center' }}>
          <Switch
            checked={addLabels}
            onChange={(_, checked) => onAddLabelsChange(checked)}
            disabled={isLoading}
          />
        </Flex>
      </FormField>
    )}

    <FormField
      title="Branch name prefix"
      description="Custom prefix added before the branch name in pull requests (e.g. feature/)"
      optional
    >
      <TextField
        value={branchPrefix}
        onChange={(e) => onBranchPrefixChange(e.target.value)}
        placeholder="e.g. elementary-"
        fullWidth
        size="large"
        disabled={isLoading}
      />
    </FormField>

    <FormField
      title="Require ticket ID"
      description="When enabled, a ticket ID must be provided for every pull request opened by Elementary — it will be prepended to the branch name and PR title"
      optional
    >
      <Flex sx={{ alignItems: 'center' }}>
        <Switch
          checked={requireTicketId}
          onChange={(_, checked) => onRequireTicketIdChange(checked)}
          disabled={isLoading}
        />
      </Flex>
    </FormField>

    <FlexColumn sx={{ gap: 0.5 }}>
      <PreviewLine
        title="Example branch name"
        value={buildBranchPreview(branchPrefix, requireTicketId)}
      />
      <PreviewLine
        title="Example PR title"
        value={buildTitlePreview(requireTicketId)}
      />
    </FlexColumn>

    <FormField
      title="Commit message prefix"
      description="Custom prefix added to every commit message (e.g. [elementary])"
      optional
    >
      <TextField
        value={commitMessagePrefix}
        onChange={(e) => onCommitMessagePrefixChange(e.target.value)}
        placeholder="e.g. [elementary]"
        fullWidth
        size="large"
        disabled={isLoading}
      />
    </FormField>

    <PreviewLine
      title="Example commit message"
      value={buildCommitPreview(commitMessagePrefix)}
    />
  </FlexColumn>
);

export default SettingsSection;
